import type { AsyncState } from '@cartwhl/common/types';

export type SWRApiResponse<T> = {
  data?: T | null;
  error?: { message: string } | null;
};

export type SWRCacheEntry<T> = {
  data: T;
  updatedAt: number;
  inFlight?: Promise<SWRApiResponse<T>>;
};

export type SWRCache<T> = {
  get: (key: string) => SWRCacheEntry<T> | undefined;
  set: (key: string, entry: SWRCacheEntry<T>) => void;
  delete: (key: string) => void;
  clear: () => void;
};

export type SWRLoaderOptions<T, TArgs extends unknown[]> = {
  cache: SWRCache<T>;
  cacheKey: (...args: TArgs) => string;
  ttlMs: number;
};

export type SWRLoader<TArgs extends unknown[], T> = {
  /** Load data, using fresh cached data when available. */
  load: (...args: TArgs) => Promise<T | undefined>;
  /** Force a network request while still hydrating cached data first. */
  refresh: (...args: TArgs) => Promise<T | undefined>;
  /** Reset only the bound state, leaving shared cache entries intact. */
  reset: () => void;
  /** Delete one cache entry. */
  invalidate: (...args: TArgs) => void;
  /** Merge external/incremental updates into state and cache. */
  updateData: (
    args: TArgs,
    updater: (currentData: T | undefined) => T | undefined,
  ) => void;
  /** Read one cache entry without mutating state. */
  getCachedData: (...args: TArgs) => T | undefined;
};

export type CreateSWRLoaderOptions<T, TArgs extends unknown[]> = {
  getState: () => AsyncState<T>;
  setState: (value: AsyncState<T>) => void;
  fetcher: (...args: TArgs) => Promise<SWRApiResponse<T>>;
  swr: SWRLoaderOptions<T, TArgs>;
};

export function createSWRCache<T>(): SWRCache<T> {
  const entries = new Map<string, SWRCacheEntry<T>>();

  return {
    get: (key: string) => entries.get(key),
    set: (key: string, entry: SWRCacheEntry<T>) => {
      entries.set(key, entry);
    },
    delete: (key: string) => {
      entries.delete(key);
    },
    clear: () => {
      entries.clear();
    },
  };
}

export function createSWRLoader<T, TArgs extends unknown[]>({
  getState,
  setState,
  fetcher,
  swr,
}: CreateSWRLoaderOptions<T, TArgs>): SWRLoader<TArgs, T> {
  const inFlightByKey = new Map<string, Promise<SWRApiResponse<T>>>();

  const clearInFlight = (
    cacheKey: string,
    inFlight: Promise<SWRApiResponse<T>>,
  ) => {
    if (inFlightByKey.get(cacheKey) === inFlight) {
      inFlightByKey.delete(cacheKey);
    }

    const cacheEntry = swr.cache.get(cacheKey);
    if (cacheEntry?.inFlight === inFlight) {
      swr.cache.set(cacheKey, {
        data: cacheEntry.data,
        updatedAt: cacheEntry.updatedAt,
      });
    }
  };

  const load = async (
    force: boolean,
    ...args: TArgs
  ): Promise<T | undefined> => {
    const cacheKey = swr.cacheKey(...args);
    const cachedEntry = swr.cache.get(cacheKey);
    const now = Date.now();

    if (cachedEntry) {
      setState({ status: 'ready', data: cachedEntry.data });

      if (!force && now - cachedEntry.updatedAt < swr.ttlMs) {
        return cachedEntry.data;
      }
    } else {
      setState({ status: 'pending', data: getState().data });
    }

    let inFlight = swr.cache.get(cacheKey)?.inFlight ?? inFlightByKey.get(cacheKey);

    if (!inFlight) {
      inFlight = fetcher(...args);
      inFlightByKey.set(cacheKey, inFlight);

      const entry = swr.cache.get(cacheKey);
      if (entry) {
        swr.cache.set(cacheKey, { ...entry, inFlight });
      }
    }

    const { data, error } = await inFlight;
    clearInFlight(cacheKey, inFlight);

    const latestCachedEntry = swr.cache.get(cacheKey);

    if (error) {
      if (latestCachedEntry) {
        setState({ status: 'ready', data: latestCachedEntry.data });
        return latestCachedEntry.data;
      }

      setState({ status: 'error', error: error.message, data: undefined });
      return undefined;
    }

    if (data !== undefined && data !== null) {
      const nextEntry = { data, updatedAt: Date.now() };
      swr.cache.set(cacheKey, nextEntry);
      setState({ status: 'ready', data });
      return data;
    }

    return undefined;
  };

  return {
    load: async (...args: TArgs) => {
      return await load(false, ...args);
    },
    refresh: async (...args: TArgs) => {
      return await load(true, ...args);
    },
    reset: () => {
      setState({ status: 'initial', data: undefined });
    },
    invalidate: (...args: TArgs) => {
      swr.cache.delete(swr.cacheKey(...args));
    },
    updateData: (args, updater) => {
      const cacheKey = swr.cacheKey(...args);
      const cachedEntry = swr.cache.get(cacheKey);
      const currentData = cachedEntry?.data ?? getState().data;
      const nextData = updater(currentData);

      if (nextData === undefined) {
        return;
      }

      swr.cache.set(cacheKey, {
        data: nextData,
        updatedAt: Date.now(),
        ...(cachedEntry?.inFlight ? { inFlight: cachedEntry.inFlight } : {}),
      });
      setState({ status: 'ready', data: nextData });
    },
    getCachedData: (...args: TArgs) => {
      return swr.cache.get(swr.cacheKey(...args))?.data;
    },
  };
}
